import { useState, useMemo, useCallback } from 'react';
import LiveRadioCard from './components/LiveRadioCard';
import LiveSortBar from './components/LiveSortBar';
import { liveRadios, liveRadiosStats, type SortMode } from '@/mocks/liveRadios';

export default function LiveRadiosPage() {
  const [sortMode, setSortMode] = useState<SortMode>('activity');

  const sortedRadios = useMemo(() => {
    const list = [...liveRadios];
    if (sortMode === 'activity') {
      list.sort((a, b) => b.activityScore - a.activityScore);
    } else if (sortMode === 'interaction') {
      list.sort((a, b) => b.interactionScore - a.interactionScore);
    } else {
      list.sort((a, b) => b.currentListeners - a.currentListeners);
    }
    return list;
  }, [sortMode]);

  const totalListeners = useMemo(
    () => liveRadios.reduce((sum, r) => sum + r.currentListeners, 0),
    []
  );

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20dynamic%20live%20radio%20broadcast%20hub%20with%20glowing%20audio%20waveform%20visualizations%20neon%20accent%20lighting%20warm%20amber%20and%20red%20tones%20professional%20broadcast%20studio%20atmosphere%20multiple%20studio%20rooms%20visible%20through%20glass%20panels%20energetic%20real%20time%20broadcast%20energy%20flowing%20sound%20waves%20abstract%20dark%20atmospheric%20background%20with%20subtle%20radio%20tower%20silhouettes%20modern%20media%20center%20aesthetic&width=1600&height=900&seq=live-radios-hero-01&orientation=landscape"
          alt="Radios en Vivo Ahora"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/65"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-red-500/80 px-3 py-1 rounded-full backdrop-blur-sm">
              LIVE
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Radios en Vivo Ahora
          </h1>
          <p className="text-sm md:text-base text-background-50/75 max-w-lg leading-relaxed">
            Todas las emisoras transmitiendo en este momento. Entrá, escuchá y conectate con la comunidad en tiempo real.
          </p>
          <div className="flex items-center gap-5 mt-5">
            <div className="flex items-center gap-2 text-background-50/90 text-xs">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="font-bold">{liveRadiosStats.totalLiveRadios} en vivo</span>
            </div>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLiveListeners.toLocaleString()} oyentes</span>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLivePrograms} programas</span>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLiveBroadcasters} locutores</span>
          </div>
        </div>
      </section>

      {/* STATS ROW */}
      <section className="bg-background-100 py-5">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex flex-wrap gap-5 items-center justify-center">
          {[
            { icon: 'ri-radio-line', label: 'Radios al Aire', value: liveRadiosStats.totalLiveRadios.toString() },
            { icon: 'ri-headphone-line', label: 'Oyentes Conectados', value: liveRadiosStats.totalLiveListeners.toLocaleString() },
            { icon: 'ri-mic-line', label: 'Programas Activos', value: liveRadiosStats.totalLivePrograms.toString() },
            { icon: 'ri-user-voice-line', label: 'Locutores', value: liveRadiosStats.totalLiveBroadcasters.toString() },
            { icon: 'ri-chat-smile-2-line', label: 'Interacción Promedio', value: `${liveRadiosStats.averageInteraction}%` },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10 space-y-6">
        {/* Sort Bar */}
        <LiveSortBar
          currentSort={sortMode}
          onSortChange={setSortMode}
          totalCount={sortedRadios.length}
          totalListeners={totalListeners}
        />

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {sortedRadios.map((radio) => (
            <LiveRadioCard key={radio.id} radio={radio} />
          ))}
        </div>

        {/* Footer CTA */}
        <section className="pt-8 pb-4 text-center">
          <p className="text-xs text-foreground-400">
            ¿Tu radio no aparece?{' '}
            <a href="/register-radio" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
              Registrala en RadioFrecuenciaWeb
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}